import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Users } from 'lucide-react';
import useAuthStore from '../../../core/stores/authStore';
import { getPointService } from '../../../domains/point';
import { DUMMY_PRODUCTS, HEALTH_INTERESTS, formatAmount } from '../shared/dummyData';
import { useTargetGroups } from '../../hooks/coupon/useCouponTargeting';
import { agesToText } from '../../hooks/coupon/couponTargeting';
import useUIStore from '../../../core/stores/uiStore';
import styles from './PointDistribute.module.css';

const AGE_MAP = {
  '전체': [],
  '20대': [20],
  '30대': [30],
  '40대': [40],
  '50대': [50],
  '60대': [60],
  '70대': [70],
  '80대+': [80, 90],
};

const AGE_OPTIONS = ['전체', '20대', '30대', '40대', '50대', '60대', '70대', '80대+'];

const toApiAges = (selectedAges) => {
  if (!selectedAges || selectedAges.length === 0) return null;
  const ages = selectedAges.flatMap(a => AGE_MAP[a] || []);
  return ages.length > 0 ? ages : null;
};

const PointDistribute = () => {
  const navigate = useNavigate();
  const { pharmacy } = useAuthStore();
  const setHeaderTabs = useUIStore(state => state.setHeaderTabs);

  // 섹션1: 포인트 설정
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [expiryPreset, setExpiryPreset] = useState('1year');
  const [expiryDate, setExpiryDate] = useState('');

  // 섹션2: 타겟 그룹
  const [targetMode, setTargetMode] = useState('new');
  const [gender, setGender] = useState('ALL');
  const [selectedAges, setSelectedAges] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [saveTargetGroup, setSaveTargetGroup] = useState(false);
  // 저장 그룹 = 이벤트 만들기와 같은 GET /coupon/target-groups.
  // 포인트 지급 API 는 성별·연령만 받으므로 질환·최근 방문 조건이 있는 그룹은 제외 (조건이 무시돼 더 넓게 지급되는 것 방지)
  const { data: allGroups } = useTargetGroups();
  // 참조가 렌더마다 바뀌면 아래 대상 인원 조회 effect 가 계속 다시 돈다 → memo
  const savedGroups = useMemo(
    () => (allGroups ?? []).filter(g => !g.target_conditions?.length && !g.last_visit_days_gte),
    [allGroups]
  );

  // 건강 관심사
  const [selectedHealthInterests, setSelectedHealthInterests] = useState([]);

  // 상품 선택
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');

  // 타겟수 (API 연동)
  const [eligibleCount, setEligibleCount] = useState(0);
  const debounceRef = useRef(null);

  // 완료
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Header tabs
  useEffect(() => {
    setHeaderTabs(
      [
        { id: 'coupon', label: '쿠폰 이벤트' },
        { id: 'point', label: '포인트 이벤트' },
      ],
      'point',
      (id) => {
        if (id === 'coupon') navigate('/dashboard/coupons/distribute');
      }
    );
    return () => setHeaderTabs(null);
  }, [setHeaderTabs, navigate]);

  const isFormValid = description?.trim() && Number(amount) >= 1 && (expiryPreset !== 'custom' || expiryDate);

  const getExpiresDays = () => {
    if (expiryPreset === '1week') return 7;
    if (expiryPreset === '1month') return 30;
    if (expiryPreset === '1year') return 365;
    if (expiryPreset === 'custom' && expiryDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(expiryDate);
      const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 1;
    }
    return 365;
  };

  const genderLabel = (g) => {
    if (g === 'ALL') return '전체';
    if (g === 'MALE') return '남성';
    if (g === 'FEMALE') return '여성';
    return g;
  };

  const handleAgeToggle = (age) => {
    if (age === '전체') {
      setSelectedAges([]);
      return;
    }
    setSelectedAges(prev =>
      prev.includes(age) ? prev.filter(a => a !== age) : [...prev, age]
    );
  };

  const handleHealthToggle = (h) => {
    setSelectedHealthInterests(prev =>
      prev.includes(h) ? prev.filter(x => x !== h) : [...prev, h]
    );
  };

  const handleProductToggle = (id) => {
    setSelectedProducts(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const filteredProducts = DUMMY_PRODUCTS.filter(p =>
    !productSearch || p.name.includes(productSearch) || p.category.includes(productSearch)
  );

  const getCurrentTarget = useCallback(() => {
    if (targetMode === 'saved' && selectedGroupId) {
      const group = savedGroups.find(g => g.id === selectedGroupId);
      if (group) return { gender: group.target_gender || 'ALL', apiAges: group.target_ages?.length ? group.target_ages : null };
    }
    return { gender, apiAges: toApiAges(selectedAges) };
  }, [targetMode, selectedGroupId, gender, selectedAges, savedGroups]);

  // eligible-count API 호출 (디바운스 300ms)
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      const pharmacyId = pharmacy?.business_registration_number;
      if (!pharmacyId) return;

      const { gender: g, apiAges } = getCurrentTarget();
      const params = {};
      if (g !== 'ALL') {
        params.target_gender = g;
      }
      if (apiAges) {
        params.target_ages = apiAges.join(',');
      }

      try {
        const pointService = getPointService();
        const result = await pointService.getEligibleCount(pharmacyId, params);
        setEligibleCount(result?.data?.total_eligible ?? 0);
      } catch {
        setEligibleCount(0);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [pharmacy, getCurrentTarget]);

  const getTargetInfo = () => {
    if (targetMode === 'saved' && selectedGroupId) {
      const group = savedGroups.find(g => g.id === selectedGroupId);
      if (group) return { gender: group.target_gender || 'ALL', label: group.name };
    }
    const ageLabel = selectedAges.length === 0 ? '전체' : selectedAges.join(', ');
    return {
      gender,
      selectedAges,
      label: `${genderLabel(gender)}, ${ageLabel}`,
    };
  };

  const handleSubmit = async () => {
    setErrorMessage('');

    if (!description?.trim()) {
      setErrorMessage('포인트 설명을 입력해주세요.');
      return;
    }
    if (!amount || Number(amount) < 1) {
      setErrorMessage('포인트 금액을 입력해주세요. (최소 1P)');
      return;
    }
    if (expiryPreset === 'custom' && !expiryDate) {
      setErrorMessage('만료일을 선택해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);

      const pointService = getPointService();
      const { gender: g, apiAges } = getCurrentTarget();

      const requestData = {
        pharmacy_id: pharmacy?.business_registration_number,
        amount: Number(amount),
        description: description || undefined,
        expires_days: getExpiresDays(),
        target_gender: g,
        target_ages: apiAges || undefined,
      };

      const response = await pointService.earnPoints(requestData);

      if (response?.result_code === '200') {
        setResultData(response.data);
        setIsCompleted(true);
      } else {
        setErrorMessage(response?.result_msg || '포인트 지급에 실패했습니다.');
      }
    } catch (error) {
      console.error('포인트 지급 실패:', error);
      setErrorMessage(error.message || '포인트 지급에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setDescription('');
    setAmount('');
    setExpiryPreset('1year');
    setExpiryDate('');
    setTargetMode('new');
    setGender('ALL');
    setSelectedAges([]);
    setSelectedGroupId(null);
    setSaveTargetGroup(false);
    setIsCompleted(false);
    setResultData(null);
    setErrorMessage('');
  };

  const targetInfo = getTargetInfo();

  return (
    <div className={styles.container}>
      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}

      {isCompleted ? (
        <div style={{ padding: '24px' }}>
          <div className={styles.sectionCard}>
            <div className={styles.completeSection}>
              <div className={styles.completeIcon}><CheckCircle size={56} color="#4CAF50" /></div>
              <h3 className={styles.completeTitle}>포인트 지급 완료!</h3>
              <p className={styles.completeDesc}>
                {formatAmount(Number(amount))}P 포인트가 지급되었습니다.
              </p>
              {resultData && (
                <div className={styles.resultGrid}>
                  <div className={styles.resultItem}>
                    <div className={styles.resultLabel}>승인번호</div>
                    <div className={styles.resultValue}>{resultData.approval_number || '-'}</div>
                  </div>
                  <div className={styles.resultItem}>
                    <div className={styles.resultLabel}>잔여 포인트</div>
                    <div className={styles.resultValue}>{formatAmount(resultData.available_points || 0)}P</div>
                  </div>
                </div>
              )}
              <button className={styles.resetButton} onClick={handleReset}>
                새 포인트 지급
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.contentLayout}>
          {/* 왼쪽 컬럼 */}
          <div className={styles.mainColumn}>
            {/* 카드1: 포인트 설정 */}
            <div className={styles.sectionCard}>
              <div className={styles.cardHeader}>
                <div>
                  <h3 className={styles.cardTitle}>포인트 설정</h3>
                  <p className={styles.cardDesc}>지급할 포인트 정보를 입력하세요.</p>
                </div>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>쿠폰 지급 사유</span>
                  <textarea
                    className={styles.fieldTextarea}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="포인트 지급 사유를 입력하세요"
                    rows={3}
                    maxLength={200}
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>포인트 금액</span>
                  <div className={styles.inputWithSuffix}>
                    <input
                      type="number"
                      className={styles.fieldInput}
                      value={amount}
                      onChange={e => setAmount(e.target.value)}
                      placeholder="예: 1000"
                      min="1"
                    />
                    <span className={styles.inputSuffix}>P</span>
                  </div>
                </div>

                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>유효기간</span>
                  <div className={styles.pillGroup}>
                    {[
                      { value: '1week', label: '1주' },
                      { value: '1month', label: '1개월' },
                      { value: '1year', label: '1년' },
                      { value: 'custom', label: '기타' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        className={`${styles.pill} ${expiryPreset === opt.value ? styles.pillActive : ''}`}
                        onClick={() => setExpiryPreset(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {expiryPreset === 'custom' && (
                    <input
                      type="date"
                      className={`${styles.fieldInput} ${styles.expiryDateInput}`}
                      value={expiryDate}
                      onChange={e => setExpiryDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* 카드2: 타겟 그룹 설정 */}
            <div className={styles.sectionCard}>
              <div className={styles.cardHeader}>
                <div>
                  <h3 className={styles.cardTitle}>타겟 그룹 설정</h3>
                  <p className={styles.cardDesc}>포인트를 지급할 대상을 선택하세요.</p>
                </div>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>타겟 설정</span>
                  <div className={styles.pillGroup}>
                    <button
                      className={`${styles.pill} ${targetMode === 'new' ? styles.pillActive : ''}`}
                      onClick={() => setTargetMode('new')}
                    >
                      직접 설정
                    </button>
                    <button
                      className={`${styles.pill} ${targetMode === 'saved' ? styles.pillActive : ''}`}
                      onClick={() => setTargetMode('saved')}
                    >
                      저장된 타겟 불러오기
                    </button>
                  </div>
                </div>

                {targetMode === 'new' ? (
                  <>
                    <div className={styles.fieldGroup}>
                      <span className={styles.fieldLabel}>성별</span>
                      <div className={styles.pillGroup}>
                        {[
                          { value: 'ALL', label: '전체' },
                          { value: 'MALE', label: '남성' },
                          { value: 'FEMALE', label: '여성' },
                        ].map(g => (
                          <button
                            key={g.value}
                            className={`${styles.pill} ${gender === g.value ? styles.pillActive : ''}`}
                            onClick={() => setGender(g.value)}
                          >
                            {g.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className={styles.fieldGroup}>
                      <span className={styles.fieldLabel}>연령대</span>
                      <div className={styles.pillGroup}>
                        {AGE_OPTIONS.map(a => (
                          <button
                            key={a}
                            className={`${styles.pill} ${
                              a === '전체'
                                ? selectedAges.length === 0 ? styles.pillActive : ''
                                : selectedAges.includes(a) ? styles.pillActive : ''
                            }`}
                            onClick={() => handleAgeToggle(a)}
                          >
                            {a}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className={styles.fieldGroup}>
                      <span className={styles.fieldLabel}>설정된 타겟</span>
                      <div className={styles.targetReadonly}>
                        {targetInfo.label}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={styles.groupSection}>
                    {savedGroups.map(group => (
                      <div
                        key={group.id}
                        className={`${styles.groupCard} ${selectedGroupId === group.id ? styles.groupActive : ''}`}
                        onClick={() => setSelectedGroupId(group.id)}
                      >
                        <div className={styles.groupName}>{group.name}</div>
                        <div className={styles.groupInfo}>
                          {genderLabel(group.target_gender || 'ALL')} / {agesToText(group.target_ages) || '전체'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 체크박스: 이 타겟 그룹 저장하기 */}
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={saveTargetGroup}
                    onChange={e => setSaveTargetGroup(e.target.checked)}
                  />
                  <span className={styles.checkboxCustom} />
                  <span className={styles.checkboxText}>이 타겟 그룹 저장하기</span>
                </label>
              </div>
            </div>

            {/* 카드: 건강 관심사 */}
            <div className={styles.sectionCard}>
              <div className={styles.cardHeader}>
                <div>
                  <h3 className={styles.cardTitle}>건강 관심사</h3>
                  <p className={styles.cardDesc}>관련 건강 관심사를 선택하세요.</p>
                </div>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.pillGroup} style={{ flexWrap: 'wrap' }}>
                  {HEALTH_INTERESTS.map(h => (
                    <button
                      key={h}
                      className={`${styles.pill} ${selectedHealthInterests.includes(h) ? styles.pillActive : ''}`}
                      onClick={() => handleHealthToggle(h)}
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* 카드: 상품 선택 */}
            <div className={styles.sectionCard}>
              <div className={styles.cardHeader}>
                <div>
                  <h3 className={styles.cardTitle}>상품 선택</h3>
                  <p className={styles.cardDesc}>포인트 적용 대상 상품을 선택하세요.</p>
                </div>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.fieldGroup}>
                  <input
                    type="text"
                    className={styles.fieldInput}
                    value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    placeholder="상품명 또는 카테고리 검색..."
                  />
                </div>
                <div style={{ maxHeight: '240px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {filteredProducts.map(p => (
                    <label
                      key={p.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                        border: selectedProducts.includes(p.id) ? '1.5px solid #3B82F6' : '1px solid #E2E8F0',
                        background: selectedProducts.includes(p.id) ? '#EFF6FF' : '#fff',
                        transition: 'all 0.15s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(p.id)}
                        onChange={() => handleProductToggle(p.id)}
                        style={{ accentColor: '#3B82F6' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1E293B' }}>{p.name}</div>
                        <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>
                          {p.company} · {p.category} · 재고 {p.stock}개
                        </div>
                      </div>
                      <div style={{ fontSize: 'var(--text-12)', fontWeight: 600, color: '#3B82F6' }}>
                        {p.price.toLocaleString()}원
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

          </div>

          {/* 오른쪽 사이드 카드 */}
          <div className={styles.sideColumn}>
            <div className={styles.targetPanel}>
              <div className={styles.targetPanelHeader}>
                <div className={styles.targetPanelIconBadge}>
                  <Users className={styles.targetPanelIcon} />
                </div>
                <span className={styles.targetPanelLabel}>대상 고객 수</span>
              </div>
              <div className={styles.targetPanelCount}>
                약 {formatAmount(eligibleCount)}명
              </div>
              <div className={styles.targetPanelDivider} />
              <ul className={styles.targetPanelNotes}>
                <li>선택한 타겟에게 노출되기까지 최대 1일이 걸릴 수 있어요.</li>
                <li>서비스 상황에 따라 타겟수가 달라질 수 있어요.</li>
                <li>타겟 조건 추가 시, 사용자 설정에 따라 타겟수가 달라질 수 있어요.</li>
                <li>설정한 조건의 타겟수는 시간이 지나 바뀔 수 있어요.</li>
              </ul>
            </div>

            {/* 지급하기 버튼 */}
            <button
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormValid}
            >
              {isSubmitting ? '지급 중...' : '지급하기'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointDistribute;
